import React, { useState, useEffect, useMemo } from 'react';
import { Box, Typography, Alert } from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import CustomButton from '../../../components/@extended/CustomButton';
import CreateQueryForm from './query/CreateQueryForm';
import QueryList from './query/QueryList';
import QueryDetail from './query/QueryDetail';
import ConfirmationDialog from '../../../components/common/ConfirmationDialog';
import { bloqcibeApi } from '../../../store/slices/apiSlice';
import { useDispatch, useSelector } from 'react-redux';
import { openMessage } from '../../../store/slices/showMessageSlice';
import { getNestApiErrorMessage } from '../../../util/util';

const QueryTab = ({
  sponsorId,
  trialId,
  siteId,
  trialSiteId,
  subjectMasterId,
  crfMasterId,
  stepKey,
  sectionKey,
  subSectionKey,
  categoryKey,
  fieldKey,
  fieldLabel,
  stepName,
  sectionName,
  subSectionName,
  categoryName,
  users,
  userSiteTrialRole
}) => {
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const trialUsers = useSelector((state) => state.trial.trialUsers);
  const loggedInUser = useSelector((state) => state.userDetails.loggedInUser);
  const [showForm, setShowForm] = useState(false);
  const [selectedQuery, setSelectedQuery] = useState(null);
  const [getQueries] = bloqcibeApi.endpoints.getQueries.useLazyQuery();
  const [queries, setQueries] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showConfirmationDialog, setShowConfirmationDialog] = useState(false);
  // const [users, setUsers] = useState([]);

  useEffect(() => {
    if (fieldKey) {
      fetchQueries();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fieldKey]);

  // Check for queryId in URL and auto-open query details
  useEffect(() => {
    const queryIdFromUrl = searchParams.get('queryId');
    if (queryIdFromUrl && queries.length > 0) {
      const query = queries.find((q) => q.id === queryIdFromUrl);
      if (query && !selectedQuery) {
        setSelectedQuery(query);
      }
    }
  }, [searchParams, queries, selectedQuery]);

  const fetchQueries = async () => {
    if (!fieldKey) return;

    setIsLoading(true);
    try {
      const result = await getQueries({
        sponsorId,
        fieldKey,
        trialId,
        siteId,
        subjectMasterId,
        crfMasterId,
        stepKey,
        sectionKey,
        subSectionKey,
        categoryKey,
        active: true,
      });
      if (result.data) {
        const fetchedQueries = result.data.data || [];
        setQueries(fetchedQueries);
        
        // Check for queryId in URL and auto-open query details after fetching
        const queryIdFromUrl = searchParams.get('queryId');
        if (queryIdFromUrl && fetchedQueries.length > 0 && !selectedQuery) {
          const query = fetchedQueries.find((q) => q.id === queryIdFromUrl);
          if (query) {
            setSelectedQuery(query);
          }
        }
      } else if (result.error) {
        setQueries([]);
        dispatch(
          openMessage({
            message: getNestApiErrorMessage(result.error, 'Failed to load queries'),
            messageSeverity: 'error',
          })
        );
      }
    } catch (error) {
      console.error('Error fetching queries:', error);
      setQueries([]);
      dispatch(
        openMessage({
          message: getNestApiErrorMessage(error, 'Failed to load queries'),
          messageSeverity: 'error',
        })
      );
    } finally {
      setIsLoading(false);
    }
  };

  const hasOpenQueries = useMemo(() => queries.some((q) => q.status === 'OPEN' || q.status === 'REOPENED'), [queries]);

  const handleCreateQuery = () => {
    if (hasOpenQueries) {
      dispatch(
        openMessage({
          message: 'You have queries with status OPEN / REOPENED.',
          messageSeverity: 'warning',
        })
      );
    }
    setShowForm(true);
  };

  const handleBack = () => {
    setShowForm(false);
    fetchQueries(); // Refresh queries when going back
  };

  const handleQueryCreated = () => {
    setShowForm(false);
    fetchQueries(); // Refresh queries after creation
  };

  const handleQueryClick = (query) => {
    setSelectedQuery(query);
    // Update URL with queryId
    const params = new URLSearchParams(searchParams);
    params.set('queryId', query.id);
    setSearchParams(params, { replace: true });
  };

  const handleBackFromDetail = () => {
    setSelectedQuery(null);
    // Remove queryId from URL when going back
    const params = new URLSearchParams(searchParams);
    params.delete('queryId');
    setSearchParams(params, { replace: true });
    fetchQueries(); // Refresh queries when going back
  };

  if (showForm) {
    return (
      <CreateQueryForm
        onBack={handleBack}
        sponsorId={sponsorId}
        trialId={trialId}
        siteId={siteId}
        trialSiteId={trialSiteId}
        subjectMasterId={subjectMasterId}
        crfMasterId={crfMasterId}
        stepKey={stepKey}
        sectionKey={sectionKey}
        subSectionKey={subSectionKey}
        categoryKey={categoryKey}
        fieldKey={fieldKey}
        fieldLabel={fieldLabel}
        stepName={stepName}
        sectionName={sectionName}
        subSectionName={subSectionName}
        categoryName={categoryName}
        onQueryCreated={handleQueryCreated}
        users={users}
        userSiteTrialRole={userSiteTrialRole}
      />
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: 0,
      }}
    >
      {(userSiteTrialRole === 'site_monitor' || userSiteTrialRole === 'data_manager') && !selectedQuery && (
        <Box sx={{ p: 2, flexShrink: 0 }}>
          <Box sx={{ display: 'flex', justifyContent: 'end', alignItems: 'center', mb: 2 }}>
            <CustomButton variant="contained" size="small" onClick={handleCreateQuery}>
              Create Query
            </CustomButton>
          </Box>
          {hasOpenQueries && (
            <Alert severity="info" sx={{ mb: 2 }}>
              You have queries with status OPEN / REOPENED. You can still create a new query.
            </Alert>
          )}
        </Box>
      )}
      <Box sx={{ flexGrow: 1, minHeight: 0, overflow: 'hidden' }}>
        {selectedQuery ? (
          <QueryDetail
            queryId={selectedQuery.id}
            onBack={handleBackFromDetail}
            users={users}
            sponsorId={sponsorId}
            trialId={trialId}
            siteId={siteId}
            trialSiteId={trialSiteId}
            fieldLabel={fieldLabel}
            userSiteTrialRole={userSiteTrialRole}
          />
        ) : isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
            <Typography>Loading queries...</Typography>
          </Box>
        ) : (
          <QueryList queries={queries} onQueryClick={handleQueryClick} />
        )}
      </Box>
    </Box>
  );
};

export default React.memo(QueryTab);
